/**
 * Full Profile View for Searched Users - Beautiful Redesign
 * Displays comprehensive profile information in an organized, attractive layout
 * Left side: Basic profile info card, Right side: Photo gallery
 * Below: Detailed sections in beautiful cards
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { expressInterest, checkExistingInterest } from '@/services/profileInterest';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ArrowLeft, Calendar, MapPin, Briefcase, Phone, Mail, Home, Users, FileText, Video, 
  Heart, MessageCircle, Star, CheckCircle, User, Cake, Scale, GraduationCap, 
  Building, Eye, ChevronLeft, ChevronRight, X, Download, Share2, 
  Activity, Shield, Award, Globe, Clock, UserCheck, HeartHandshake, AlertCircle, ZoomIn, Camera
} from 'lucide-react';

import ChatModal from '@/components/ChatModal';
import { getApiBaseUrl, getMediaUrl } from '@/config/api';

type BasicInfo = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNo: string;
  gender: string;
  dob?: string;
  profession?: string;
  city?: string;
  state?: string;
  country?: string;
  height?: number;
  weight?: number;
  religion?: string;
  caste?: string;
  motherTongue?: string;
  maritalStatus?: string;
  education?: string;
  isVerified?: boolean;
  isPremium?: boolean;
  isOnline?: boolean;
  lastActive?: string;
  createdAt?: string;
  overallStatus?: string;
};

type PersonalDetails = {
  aboutMe?: string;
  bodyType?: string;
  complexion?: string;
  physicalStatus?: string;
  bloodGroup?: string;
  diet?: string;
  smoke?: string;
  drink?: string;
  hobbies?: string;
  interests?: string;
  education?: string;
  educationField?: string;
  workExperience?: string;
  annualIncome?: string;
  employedIn?: string;
  occupation?: string;
  jobLocation?: string;
  organization?: string;
  workingSince?: string;
  // Astrological fields
  subCaste?: string;
  gotra?: string;
  manglik?: string;
  horoscopeMatch?: string;
  birthTime?: string;
  birthPlace?: string;
  rashi?: string;
  nakshatra?: string;
  charan?: string;
  gan?: string;
  nadi?: string;
};

type FamilyDetails = {
  fatherName?: string;
  fatherOccupation?: string;
  fatherEducation?: string;
  fatherAlive?: boolean;
  motherName?: string;
  motherOccupation?: string;
  motherEducation?: string;
  motherAlive?: boolean;
  brothersCount?: number;
  sistersCount?: number;
  marriedBrothers?: number;
  marriedSisters?: number;
  unmarriedBrothers?: number;
  unmarriedSisters?: number;
  familyType?: string;
  familyStatus?: string;
  familyValues?: string;
  familyIncome?: string;
  aboutFamily?: string;
  nativePlace?: string;
  ancestralOrigin?: string;
  familyLivingIn?: string;
  familyProperty?: string;
  familyBusiness?: string;
  noOfFamilyMembers?: number;
};

type AddressDetails = {
  buildingNo?: string;
  buildingName?: string;
  areaName?: string;
  street?: string;
  landmark?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
  residence?: string;
  addressType?: string;
};

type PartnerExpectations = {
  ageRange?: { min?: number; max?: number };
  heightRange?: { min?: number; max?: number };
  maritalStatus?: string[];
  religion?: string[];
  caste?: string[];
  motherTongue?: string[];
  education?: string[];
  profession?: string[];
  annualIncome?: string[];
  location?: string[];
  diet?: string[];
  smoke?: string[];
  drink?: string[];
  manglik?: string[];
  familyValues?: string[];
  familyType?: string[];
  familyStatus?: string[];
  horoscopeMatch?: string;
  aboutPartner?: string;
};

type Photo = {
  id: number;
  userId: string;
  photoFileName: string;
  photoUrl: string;
  photoSize: number;
  photoFormat: string;
  isProfileDP: boolean;
  uploadDate?: string;
};

type Document = {
  id: number;
  userId: string;
  documentType: string;
  documentUrl: string;
  documentFileName: string;
  documentSize: number;
  documentFormat: string;
  createdAt?: string;
};

type Video = {
  id: number;
  userId: string;
  videoTitle?: string;
  videoUrl: string;
  videoFileName: string;
  videoSize: number;
  videoFormat: string;
  videoDuration?: string;
  status?: string;
  createdAt?: string;
};

type CompleteProfile = {
  basic: BasicInfo;
  personal: PersonalDetails;
  family: FamilyDetails;
  address: AddressDetails;
  partnerExpectations: PartnerExpectations;
  photos: Photo[];
  documents: Document[];
  videos: Video[];
};

const API_BASE = getApiBaseUrl();
const API = `${API_BASE}/api`;

export default function UserSearchProfile() {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  
  const [profile, setProfile] = useState<CompleteProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [showFullPhoto, setShowFullPhoto] = useState(false);
  const [selectedSection, setSelectedSection] = useState<string>('personal');
  const [userPaymentStatus, setUserPaymentStatus] = useState({
    paymentDone: false,
    profileValidity: null,
    canViewContacts: false
  });

  /**
   * Chat state
   * - chatOpen: controls modal visibility
   * - chatConversationWith: minimal target info (id + name)
   */
  const [chatOpen, setChatOpen] = useState(false);
  const [chatConversationWith, setChatConversationWith] = useState<{ id: string; name: string } | null>(null);

  /**
   * interestExpressed
   *
   * @description Tracks whether the logged-in user has successfully expressed interest
   * in this profile during the current session. When true, the main action button will
   * show "Expressed Interest" and be disabled to avoid duplicate clicks.
   */
  const [interestExpressed, setInterestExpressed] = useState<boolean>(false);

  /** Check logged-in user's payment status */
  async function checkUserPaymentStatus() {
    try {
      // Get logged-in user data from localStorage
      const storedUserData = localStorage.getItem('userData');
      
      if (!storedUserData) {
        // Set default restricted state for non-logged users
        setUserPaymentStatus({
          paymentDone: false,
          profileValidity: null,
          canViewContacts: false
        });
        return;
      }

      const userData = JSON.parse(storedUserData);
      const loggedInUserId = userData.userId;
      
      if (!loggedInUserId) {
        setUserPaymentStatus({
          paymentDone: false,
          profileValidity: null,
          canViewContacts: false
        });
        return;
      }
      
      try {
        // Fetch user's payment status from API
        const response = await fetch(`${API}/users/payment-status/${encodeURIComponent(loggedInUserId)}`);
        
        if (!response.ok) {
          setUserPaymentStatus({
            paymentDone: false,
            profileValidity: null,
            canViewContacts: false
          });
          return;
        }

        const result = await response.json();

        if (result.success && result.data) {
          const { paymentDone, profileValidity } = result.data;
          
          // Normalize paymentDone to boolean
          let isPaymentValid = false;
          if (paymentDone === true || paymentDone === 1 || paymentDone === '1') {
            isPaymentValid = true;
          }
          
          // Final validation: Both payment AND valid date required
          let canView = false;
          
          // Step 1: Check if payment was made (paymentDone = 1)
          if (!isPaymentValid) {
            canView = false;
          } else {
            // Step 2: Check if ProfileValidity exists
            if (!profileValidity || profileValidity === null || profileValidity === '') {
              canView = false;
            } else {
              // Step 3: Check if ProfileValidity is in future (not expired)
              try {
                const profileDate = new Date(profileValidity);
                const currentDate = new Date();
                const isDateValid = profileDate > currentDate;
                
                canView = isDateValid;
              } catch (e) {
                canView = false;
              }
            }
          }
          
          setUserPaymentStatus({
            paymentDone: isPaymentValid,
            profileValidity,
            canViewContacts: canView
          });
        }
      } catch (apiError) {
        // Fallback: Check if user data exists in localStorage with payment info
        try {
          const userData = JSON.parse(localStorage.getItem('userData') || '{}');
          
          // Check if userData has payment info
          const fallbackPayment = userData.paymentDone || userData.payment_status || false;
          const fallbackValidity = userData.ProfileValidity || userData.profileValidity || null;
          
          let isPaymentValid = false;
          if (fallbackPayment === true || fallbackPayment === 1 || fallbackPayment === '1') {
            isPaymentValid = true;
          }
          
          // Same validation logic as main logic
          let canView = false;
          if (!isPaymentValid) {
            canView = false;
          } else {
            if (!fallbackValidity || fallbackValidity === null || fallbackValidity === '') {
              canView = false;
            } else {
              try {
                const profileDate = new Date(fallbackValidity);
                const currentDate = new Date();
                const isDateValid = profileDate > currentDate;
                
                canView = isDateValid;
              } catch (e) {
                canView = false;
              }
            }
          }
          
          setUserPaymentStatus({
            paymentDone: isPaymentValid,
            profileValidity: fallbackValidity,
            canViewContacts: canView
          });
          
        } catch (fallbackError) {
          setUserPaymentStatus({
            paymentDone: false,
            profileValidity: null,
            canViewContacts: false
          });
        }
      }
    } catch (error) {
      setUserPaymentStatus({
        paymentDone: false,
        profileValidity: null,
        canViewContacts: false
      });
    }
  }

  /** Fetch complete profile using NEW dedicated UserSearchProfile endpoint */
  async function fetchProfile() {
    try {
      if (!userId) {
        throw new Error('Missing userId');
      }
      setLoading(true);
      
      try {
        const profileRes = await fetch(`${API}/usersearchprofile/${encodeURIComponent(userId)}`);
        
        if (!profileRes.ok) {
          const errorText = await profileRes.text();
          throw new Error(`HTTP ${profileRes.status}: ${profileRes.statusText}`);
        }
        
        const profileResult = await profileRes.json();
        
        if (!profileResult.success) {
          throw new Error(profileResult.message || 'API returned failure');
        }
        
        if (!profileResult.data) {
          throw new Error('No profile data received');
        }
        
        const completeProfile = profileResult.data;
        
        console.log('📥 [UserSearchProfile] Received profile data:', completeProfile);
        console.log('📥 [UserSearchProfile] Personal details:', completeProfile.personal);
        console.log('📥 [UserSearchProfile] Astrological fields:', {
          birthTime: completeProfile.personal?.birthTime,
          birthPlace: completeProfile.personal?.birthPlace,
          rashi: completeProfile.personal?.rashi,
          nakshatra: completeProfile.personal?.nakshatra,
          charan: completeProfile.personal?.charan,
          gan: completeProfile.personal?.gan,
          nadi: completeProfile.personal?.nadi
        });
        
        // Set complete profile data directly
        setProfile({
          basic: completeProfile.basic,
          personal: completeProfile.personal,
          family: completeProfile.family,
          address: completeProfile.address,
          partnerExpectations: completeProfile.partnerExpectations,
          photos: completeProfile.photos,
          documents: completeProfile.documents,
          videos: completeProfile.videos || []
        });
        
      } catch (apiError) {
        // Set error state
        setProfile({
          basic: {
            userId: userId || '',
            firstName: 'API Error',
            lastName: 'Service Unavailable',
            email: '',
            profession: 'Please try again',
            dob: '1995-01-01',
            gender: '—',
            height: 170,
            weight: 65,
            religion: '—',
            caste: '—',
            motherTongue: '—',
            maritalStatus: '—',
            overallStatus: 'api_error'
          },
          personal: { aboutMe: 'Unable to load profile data. Please refresh the page.' },
          family: {},
          address: {},
          partnerExpectations: {},
          photos: [],
          documents: [],
          videos: []
        });
      }
    } catch (err) {
      // This should only happen due to network errors
      setProfile({
        basic: {
          userId: userId || '',
          firstName: 'Network Error',
          lastName: 'Connection Failed',
          email: '',
          profession: 'Check Internet',
          dob: '1995-01-01',
          gender: '—',
          height: 170,
          weight: 65,
          religion: '—',
          caste: '—',
          motherTongue: '—',
          maritalStatus: '—',
          overallStatus: 'network_error'
        },
        personal: { aboutMe: 'Network error occurred while fetching profile data.' },
        family: {},
        address: {},
        partnerExpectations: {},
        photos: [],
        documents: [],
        videos: []
      });
    } finally {
      setLoading(false);
    }
  }

  /** Fetch on mount */
  /**
   * On mount when userId changes:
   * - Fetch profile
   * - Check logged-in user's payment status
   * - Check if logged-in user has already expressed interest for this profile and lock the button if so
   */
  useEffect(() => {
    if (!userId) return;

    (async () => {
      await fetchProfile();
      await checkUserPaymentStatus();

      try {
        const stored = localStorage.getItem('userData');
        if (!stored) return;
        const user = JSON.parse(stored || '{}');
        const loggedInUserId = user.userId;
        if (!loggedInUserId) return;

        // Check whether an interest record already exists (pending/previous)
        const exists = await checkExistingInterest(loggedInUserId, userId);
        if (exists) {
          setInterestExpressed(true);
        }
      } catch (err) {
        // ignore check errors — do not block rendering
        // eslint-disable-next-line no-console
        console.warn('Failed to check existing interest on load:', err);
      }
    })();
  }, [userId]);

  /** Request to add photo functionality */
  async function handleRequestToAddPhoto() {
    try {
      // Get logged-in user data from localStorage
      const storedUserData = localStorage.getItem('userData');
      
      if (!storedUserData) {
        alert('Please login to request photo additions');
        navigate('/login');
        return;
      }

      const userData = JSON.parse(storedUserData);
      const loggedInUserId = userData.userId;
      
      if (!loggedInUserId) {
        alert('Please login to request photo additions');
        navigate('/login');
        return;
      }

      if (loggedInUserId === userId) {
        alert('You cannot request photos for your own profile');
        return;
      }

      // Check if request already exists
      try {
        const existingResponse = await fetch(`${API}/photo-requests/status/${encodeURIComponent(loggedInUserId)}/${encodeURIComponent(userId)}`);
        if (existingResponse.ok) {
          const existingData = await existingResponse.json();
          if (existingData.success && existingData.requests.length > 0) {
            const hasPending = existingData.requests.some(req => req.requestStatus === 'Pending');
            if (hasPending) {
              alert('You already have a pending request for this profile');
              return;
            }
          }
        }
      } catch (checkError) {
        console.log('Checking existing request failed:', checkError);
      }

      // Create the photo request (no specific photo available)
      const response = await fetch(`${API}/photo-requests/request-no-photo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requesterId: loggedInUserId,
          profileId: userId,
          message: 'Please Add Photo to View'
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        alert('Photo request sent successfully! The profile owner will be notified.');
      } else {
        alert(result.message || 'Failed to send photo request');
      }
    } catch (error) {
      console.error('Error sending photo request:', error);
      alert('Error sending photo request. Please try again.');
    }
  }

  const age = useMemo(() => {
    if (!profile?.basic.dob) return null;
    try {
      const birthDate = new Date(profile.basic.dob);
      const today = new Date();
      let calculatedAge = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      // Adjust age if birthday hasn't occurred this year yet
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        calculatedAge--;
      }
      
      return calculatedAge;
    } catch {
      return null;
    }
  }, [profile?.basic.dob]);

  // Check if user should show "Verified" badge based on overallStatus
  const shouldShowVerified = useMemo(() => {
    return profile?.basic.overallStatus?.toLowerCase() !== 'pending';
  }, [profile?.basic.overallStatus]);

  const headerPhoto = useMemo(() => {
    if (profile?.photos.length) {
      const primary = profile.photos.find(p => p.isProfileDP);
      const firstPhoto = profile.photos[0];
      const photo = primary || firstPhoto;
      const photoUrl = photo?.photoUrl;
      return getMediaUrl(photoUrl);
    }
    return '';
  }, [profile?.photos]);

  // Check if userId exists before proceeding
  if (!userId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <User className="w-8 h-8 text-red-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">No Profile ID</h2>
            <p className="text-gray-500">No profile ID was provided. Please go back and select a profile.</p>
            <Button onClick={() => navigate('/search')} className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Search
            </Button>

          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Loading Profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <Users className="w-8 h-8 text-red-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">Profile Not Available</h2>
            <p className="text-gray-500">Unable to load this profile at moment.</p>
            <Button onClick={() => navigate('/search')} className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Search
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Navigation Sections
  const sections = [
    { id: 'personal', label: 'Personal', icon: User, color: 'blue' },
    { id: 'family', label: 'Family Information', icon: Users, color: 'green' },
    { id: 'address', label: 'Address', icon: Home, color: 'orange' },
    { id: 'habits', label: 'Habits & Lifestyle', icon: Heart, color: 'pink' },
    { id: 'astrological', label: 'Astrological', icon: Star, color: 'yellow' },
    { id: 'partner', label: 'Partner Expectations', icon: HeartHandshake, color: 'purple' },
    { id: 'documents', label: 'Documents', icon: FileText, color: 'indigo' },
    { id: 'videos', label: 'Videos', icon: Video, color: 'purple' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Sticky header with Back button */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-purple-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => navigate('/search')}
              className="bg-white border-purple-200 hover:bg-purple-50"
              title="Back to Partner Search"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Search
            </Button>
            <h1 className="text-xl sm:text-2xl font-extrabold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
              {profile.basic.firstName} {profile.basic.lastName}
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            {shouldShowVerified && (
              <Badge className="bg-green-100 text-green-700 border-green-200">
                <CheckCircle className="w-3 h-3 mr-1" />
                Verified
              </Badge>
            )}
            {profile?.basic.isPremium && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                <Star className="w-3 h-3 mr-1" />
                Premium
              </Badge>
            )}
            {profile?.basic.isOnline && (
              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-1 animate-pulse"></div>
                Online
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Top Section: Basic Info + Photo Gallery - UPDATED LAYOUT */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          {/* Left Side - Basic Information Card (COLS 1-8) */}
          <div className="lg:col-span-8">
            <Card className="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 text-white shadow-2xl border-0 overflow-hidden relative">
              {/* Decorative Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-x-12 translate-y-12"></div>
              
              <CardHeader className="relative z-10 pb-1">
                <div className="text-center">
                  {/* Name and Status - REMOVED DP ICON */}
                  <CardTitle className="text-3xl font-bold text-white drop-shadow-lg mb-1">
                    {profile.basic.firstName} {profile.basic.lastName}
                  </CardTitle>
                  
                  {/* Status Badges - REMOVED DP ICON */}
                  <div className="flex justify-center items-center space-x-2">
                    {shouldShowVerified && (
                      <Badge className="bg-green-500 text-white border-0 text-xs">
                        <UserCheck className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                    {profile.basic.isPremium && (
                      <Badge className="bg-amber-500 text-white border-0 text-xs">
                        <Award className="w-3 h-3 mr-1" />
                        Premium
                      </Badge>
                    )}
                    {profile.basic.isOnline && (
                      <Badge className="bg-blue-500 text-white border-0 text-xs">
                        <div className="w-2 h-2 bg-white rounded-full mr-1 animate-pulse"></div>
                        Online
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="relative z-10 space-y-2">
                {/* User ID */}
                <div className="text-center">
                  <p className="text-purple-200 text-xs uppercase tracking-wide">User ID</p>
                  <p className="text-white font-mono font-semibold">{profile.basic.userId}</p>
                </div>

                {/* Basic Details Grid - UPDATED FIELDS WITH PAYMENT-BASED BLUR */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Age</p>
                    <p className="text-white font-semibold flex items-center">
                      <Cake className="w-3 h-3 mr-1" />
                      {age || '—'}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Gender</p>
                    <p className="text-white font-semibold">{profile.basic.gender || '—'}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Height</p>
                    <p className="text-white font-semibold flex items-center">
                      <Scale className="w-3 h-3 mr-1" />
                      {profile.basic.height ? `${profile.basic.height} cm` : '—'}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Weight</p>
                    <p className="text-white font-semibold flex items-center">
                      <Activity className="w-3 h-3 mr-1" />
                      {profile.basic.weight ? `${profile.basic.weight} kg` : '—'}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Religion</p>
                    <p className="text-white font-semibold">{profile.basic.religion || '—'}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Caste</p>
                    <p className="text-white font-semibold">{profile.basic.caste || '—'}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Tongue</p>
                    <p className="text-white font-semibold">{profile.basic.motherTongue || '—'}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Profession</p>
                    <p className="text-white font-semibold flex items-center">
                      <Briefcase className="w-3 h-3 mr-1" />
                      {profile.basic.profession || '—'}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Education</p>
                    <p className="text-white font-semibold flex items-center text-sm">
                      <GraduationCap className="w-3 h-3 mr-1" />
                      {profile.personal.education || '—'}
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-purple-200 text-xs uppercase">Status</p>
                    <p className="text-white font-semibold">{profile.basic.maritalStatus || '—'}</p>
                  </div>
                </div>

                 {/* Contact Info Block */}
                <div className="mt-3 pt-3 border-t border-white/20">
                  <h3 className="text-white font-semibold text-sm mb-3 flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    Contact Info
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* PAYMENT-PROTECTED CONTACT INFO */}
                    <div className={`bg-white/10 backdrop-blur-sm rounded-lg p-3 relative ${!userPaymentStatus.canViewContacts ? 'blur-sm' : ''}`}>
                      <p className="text-purple-200 text-xs uppercase">Mobile</p>
                      <p className="text-white font-semibold flex items-center text-sm">
                        <Phone className="w-3 h-3 mr-1" />
                        {userPaymentStatus.canViewContacts ? (profile.basic.mobileNo || '—') : '•••••••••'}
                      </p>
                      {!userPaymentStatus.canViewContacts && (
                        <button 
                          onClick={() => navigate('/payment')}
                          className="absolute inset-0 flex items-center justify-center bg-black/60 hover:bg-black/70 transition-colors rounded-lg cursor-pointer"
                          title="Click to upgrade to premium"
                        >
                          <span className="text-white text-xs bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1.5 rounded-full font-medium hover:from-amber-600 hover:to-orange-600 transition-all">
                            🚀 Contact details require premium membership - Click here to upgrade
                          </span>
                        </button>
                      )}
                    </div>
                    <div className={`bg-white/10 backdrop-blur-sm rounded-lg p-3 relative ${!userPaymentStatus.canViewContacts ? 'blur-sm' : ''}`}>
                      <p className="text-purple-200 text-xs uppercase">Email</p>
                      <p className="text-white font-semibold flex items-center text-sm">
                        <Mail className="w-3 h-3 mr-1" />
                        {userPaymentStatus.canViewContacts ? (profile.basic.email || '—') : '•••••••@•••••••'}
                      </p>
                      {!userPaymentStatus.canViewContacts && (
                        <button 
                          onClick={() => navigate('/payment')}
                          className="absolute inset-0 flex items-center justify-center bg-black/60 hover:bg-black/70 transition-colors rounded-lg cursor-pointer"
                          title="Click to upgrade to premium"
                        >
                          <span className="text-white text-xs bg-gradient-to-r from-amber-500 to-orange-500 px-3 py-1.5 rounded-full font-medium hover:from-amber-600 hover:to-orange-600 transition-all">
                            🚀 Contact details require premium membership - Click here to upgrade
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>


                {!userPaymentStatus.canViewContacts && (
                  <button 
                    onClick={() => navigate('/payment')}
                    className="w-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400 rounded-lg p-3 text-center hover:from-amber-500/30 hover:to-orange-500/30 transition-all cursor-pointer group"
                    title="Click to upgrade to premium and unlock contact details"
                  >
                    <p className="text-amber-200 text-xs font-medium group-hover:text-amber-100 transition-colors">
                      <Award className="w-3 h-3 inline mr-1" />
                      🔓 Contact and Address details require premium membership - Click here to upgrade
                    </p>
                  </button>
                )}

              </CardContent>
            </Card>
          </div>

          {/* Right Side - Photo Gallery (COLS 9-12) - ULTRA COMPACT */}
          <div className="lg:col-span-4">
            <Card className="shadow-2xl border-0 bg-white overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-sm">
                    <Users className="w-4 h-4 mr-2" />
                    Photo Gallery
                  </CardTitle>
                  <Badge className="bg-white/20 text-white border-white/30 text-xs">
                    {profile.photos.length} Photos
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-3">
                {profile.photos.length > 0 ? (
                  <div className="space-y-1">
                    {/* Main Photo Display - CLICKABLE FOR LARGER VIEW */}
                    <div 
                      className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer group"
                      onClick={() => setShowFullPhoto(true)}
                    >
                      <img 
                        src={getMediaUrl(profile.photos[activePhotoIndex]?.photoUrl)} 
                        alt={`Profile photo ${activePhotoIndex + 1}`}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        onError={(e) => {
                          e.currentTarget.src = '/images/RJBD.png';
                        }}
                      />
                      
                      {/* Watermark - RJBD.png at center */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <img 
                          src="/images/RJBD.png"
                          alt="RJBD Watermark"
                          className="h-16 w-auto opacity-40"
                        />
                      </div>
                      
                      {/* Zoom Overlay Icon */}
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn className="w-8 h-8 text-white" />
                      </div>
                      
                      {/* Navigation Arrows */}
                      {profile.photos.length > 1 && (
                        <>
                          <Button
                            variant="ghost"
                            className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70 rounded-full w-6 h-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivePhotoIndex((prev) => (prev - 1 + profile.photos.length) % profile.photos.length);
                            }}
                          >
                            <ChevronLeft className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70 rounded-full w-6 h-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivePhotoIndex((prev) => (prev + 1) % profile.photos.length);
                            }}
                          >
                            <ChevronRight className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                      
                      {/* Photo Counter */}
                      <div className="absolute bottom-1 right-1 bg-black/50 text-white px-1 py-0.5 rounded-full text-xs">
                        {activePhotoIndex + 1} / {profile.photos.length}
                      </div>
                      
                      {/* Profile DP Badge */}
                      {profile.photos[activePhotoIndex]?.isProfileDP && (
                        <div className="absolute top-1 left-1">
                          <Badge className="bg-purple-600 text-white text-xs px-1">
                            <Star className="w-2 h-2 mr-1" />
                            DP
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Thumbnail Gallery - ULTRA SHORT WITH HORIZONTAL SCROLL */}
                    <div className="overflow-x-auto">
                      <div className="flex gap-0.5 pb-1">
                        {profile.photos.map((photo, index) => (
                          <button
                            key={photo.id}
                            onClick={() => setActivePhotoIndex(index)}
                            className={`flex-shrink-0 w-12 h-12 rounded-sm overflow-hidden border transition-all ${
                              index === activePhotoIndex 
                                ? 'border-purple-500 ring-1 ring-purple-200' 
                                : 'border-gray-200 hover:border-purple-300'
                            }`}
                          >
                            <img 
                              src={getMediaUrl(photo.photoUrl)} 
                              alt={`Thumbnail ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-2">
                      <img 
                        src="/images/RJBD.png"
                        alt="Default Profile"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-gray-500 text-xs">No photos uploaded</p>
                  <button 
                    onClick={handleRequestToAddPhoto}
                    className="mt-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-purple-700 hover:to-pink-700 transition-all"
                  >
                    <Camera className="w-4 h-4 mr-2 inline" />
                    Request to Add Photo
                  </button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-1 mb-8">
          <div className="bg-white rounded-lg p-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg"
                onClick={async () => {
                  try {
                    // Read logged-in user data from localStorage (non-invasive change)
                    const stored = localStorage.getItem('userData');
                    if (!stored) {
                      toast.error('Please log in to express interest');
                      return;
                    }
                    const user = JSON.parse(stored || '{}');
                    const fromUserId = user.userId;
                    const firstName = user.firstName || '';
                    const lastName = user.lastName || '';
                    if (!fromUserId) {
                      toast.error('Please log in to express interest');
                      return;
                    }
                    const toUserId = profile?.basic?.userId;
                    if (!toUserId) {
                      toast.error('Profile id missing');
                      return;
                    }

                    const message = `${firstName} ${lastName} sent you interest`;

                    // Show a temporary loading toast
                    const id = toast.loading('Sending interest...');

                    const result = await expressInterest(fromUserId, toUserId, message);

                    toast.dismiss(id);

                    if (result && result.success) {
                      // Mark as expressed so UI updates
                      setInterestExpressed(true);
                      toast.success('Interest expressed successfully');
                    } else if (result && result.conflict) {
                      // 409 duplicate / already pending -> treat as already expressed
                      setInterestExpressed(true);
                      toast(
                        result.message ||
                          'An interest request already exists and is pending. Button locked as expressed.'
                      );
                    } else {
                      toast.error(result.message || 'Failed to express interest');
                    }
                  } catch (err) {
                    console.error('Express interest error:', err);
                    toast.error('Failed to send interest. Please try again.');
                  }
                }}
                disabled={interestExpressed}
              >
                <Heart className="w-5 h-5 mr-2" />
                {interestExpressed ? 'Expressed Interest' : 'Express Interest'}
              </Button>
              <Button
                type="button"
                size="lg"
                className="bg-blue-600 text-white hover:bg-blue-700 shadow-lg flex items-center justify-center"
                onClick={async () => {
                  /**
                   * @description Click handler for opening chat modal.
                   * - Allows opening chat to view messages
                   * - Payment check is now inside ChatModal for sending
                   */
                  try {
                    console.debug('[UserSearchProfile] Chat clicked');
                    
                    const toUserId = profile?.basic?.userId || '';
                    const toUserName = `${profile?.basic?.firstName || ''} ${profile?.basic?.lastName || ''}`.trim();

                    if (!toUserId) {
                      console.warn('[UserSearchProfile] Cannot open chat - missing profile id');
                      toast.error('Unable to open chat: profile id missing');
                      return;
                    }

                    // Check login state
                    const stored = localStorage.getItem('userData');
                    if (!stored) {
                      console.debug('[UserSearchProfile] User not logged in - redirecting to login');
                      toast.error('Please log in to send messages');
                      // small delay to allow toast to show
                      setTimeout(() => navigate('/login'), 300);
                      return;
                    }

                    // Open chat (payment check moved to ChatModal)
                    setChatConversationWith({ id: toUserId, name: toUserName });
                    setChatOpen(true);
                    console.debug('[UserSearchProfile] Chat modal opened for', toUserId);
                  } catch (err) {
                    // Ensure any unexpected error is visible in console and shown to user
                    // eslint-disable-next-line no-console
                    console.error('[UserSearchProfile] handleChatClick error', err);
                    toast.error('Failed to open chat. Please try again.');
                  }
                }}
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Chat
              </Button>

              <Button
                size="lg"
                disabled
                className="bg-gray-400 text-gray-200 cursor-not-allowed shadow flex items-center justify-center opacity-60"
                onClick={() => {
                  try {
                    const url = window.location.href;
                    const title = `${profile.basic.firstName} ${profile.basic.lastName}`.trim() || 'Profile';
                    // Use Web Share API when available, fallback to copy link
                    if ((navigator as any).share) {
                      (navigator as any)
                        .share({ title, url })
                        .catch(() => {
                          // ignore share failures silently
                        });
                    } else if (navigator.clipboard) {
                      navigator.clipboard.writeText(url).then(() => {
                        toast.success('Profile link copied to clipboard');
                      }).catch(() => {
                        toast('Unable to copy link');
                      });
                    } else {
                      // As a last resort, open the URL in a new tab
                      window.open(url, '_blank');
                    }
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Share profile error', err);
                    toast.error('Failed to share profile');
                  }
                }}
              >
                <Share2 className="w-5 h-5 mr-2" />
                Share Profile
              </Button>

              <Button
                size="lg"
                disabled
                className="bg-gray-400 text-gray-200 cursor-not-allowed shadow flex items-center justify-center opacity-60"
                onClick={() => {
                  try {
                    // Simple fallback: print page (user can save as PDF)
                    window.print();
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Download profile (print) error', err);
                    toast.error('Failed to download profile');
                  }
                }}
              >
                <Download className="w-5 h-5 mr-2" />
                Download Profile
              </Button>

            </div>
          </div>
        </div>

        {/* Section Navigation Tabs */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-lg p-2">
            <div className="flex flex-wrap gap-2">
              {sections.map((section) => {
                const Icon = section.icon;
                const colorMap = {
                  blue: 'from-blue-500 to-cyan-500',
                  green: 'from-green-500 to-emerald-500',
                  orange: 'from-orange-500 to-amber-500',
                  pink: 'from-pink-500 to-rose-500',
                  yellow: 'from-yellow-500 to-orange-500',
                  indigo: 'from-indigo-500 to-purple-500',
                  purple: 'from-purple-500 to-pink-500'
                };
                const gradientClass = colorMap[section.color as keyof typeof colorMap] || 'from-blue-500 to-cyan-500';
                
                return (
                  <button
                    key={section.id}
                    onClick={() => setSelectedSection(section.id)}
                    className={`flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      selectedSection === section.id
                        ? `bg-gradient-to-r ${gradientClass} text-white shadow-md`
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {section.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {/* Personal Details Section */}
          {selectedSection === 'personal' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <User className="w-6 h-6 mr-3" />
                  Personal Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* About Me */}
                {profile.personal.aboutMe && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                      <User className="w-4 h-4 mr-2 text-blue-500" />
                      About Me
                    </h3>
                    <p className="text-gray-600 bg-blue-50 p-4 rounded-lg leading-relaxed">
                      {profile.personal.aboutMe}
                    </p>
                  </div>
                )}

                {/* Physical Attributes */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">Body Type</h4>
                    <p className="text-gray-800">{profile.personal.bodyType || '—'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">Complexion</h4>
                    <p className="text-gray-800">{profile.personal.complexion || '—'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">Physical Status</h4>
                    <p className="text-gray-800">{profile.personal.physicalStatus || '—'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">Blood Group</h4>
                    <p className="text-gray-800">{profile.personal.bloodGroup || '—'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">Height</h4>
                    <p className="text-gray-800">{profile.basic.height ? `${profile.basic.height} cm` : '—'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">Weight</h4>
                    <p className="text-gray-800">{profile.basic.weight ? `${profile.basic.weight} kg` : '—'}</p>
                  </div>
                </div>

                {/* Habits & Lifestyle */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Heart className="w-4 h-4 mr-2 text-pink-500" />
                    Habits & Lifestyle
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                      <h4 className="font-medium text-green-700 mb-1">Diet</h4>
                      <p className="text-green-800">{profile.personal.diet || '—'}</p>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Smoking</h4>
                      <p className="text-orange-800">{profile.personal.smoke || '—'}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-700 mb-1">Drinking</h4>
                      <p className="text-blue-800">{profile.personal.drink || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Hobbies & Interests */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Star className="w-4 h-4 mr-2 text-yellow-500" />
                    Hobbies & Interests
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-medium text-purple-700 mb-2">Hobbies</h4>
                      <p className="text-gray-800">{profile.personal.hobbies || '—'}</p>
                    </div>
                    <div className="bg-pink-50 p-4 rounded-lg">
                      <h4 className="font-medium text-pink-700 mb-2">Interests</h4>
                      <p className="text-gray-800">{profile.personal.interests || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Education & Work */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Briefcase className="w-4 h-4 mr-2 text-green-500" />
                    Education & Work
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-700 mb-1">Education</h4>
                      <p className="text-gray-800">{profile.personal.education || '—'}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-medium text-green-700 mb-1">Work Experience</h4>
                      <p className="text-gray-800">{profile.personal.workExperience || '—'}</p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h4 className="font-medium text-amber-700 mb-1">Annual Income</h4>
                      <p className="text-gray-800">{profile.personal.annualIncome || '—'}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-medium text-purple-700 mb-1">Employed In</h4>
                      <p className="text-gray-800">{profile.personal.employedIn || '—'}</p>
                    </div>
                    <div className="bg-indigo-50 p-4 rounded-lg">
                      <h4 className="font-medium text-indigo-700 mb-1">Occupation</h4>
                      <p className="text-gray-800">{profile.personal.occupation || '—'}</p>
                    </div>
                    <div className="bg-cyan-50 p-4 rounded-lg">
                      <h4 className="font-medium text-cyan-700 mb-1">Job Location</h4>
                      <p className="text-gray-800">{profile.personal.jobLocation || '—'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Family Information Section */}
          {selectedSection === 'family' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Users className="w-6 h-6 mr-3" />
                  Family Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Parents Information */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Home className="w-4 h-4 mr-2 text-green-500" />
                    Parents Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-medium text-blue-700 mb-2">Father's Details</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Name:</span>
                            <span className="font-medium">{profile.family.fatherName || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Occupation:</span>
                            <span className="font-medium">{profile.family.fatherOccupation || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Education:</span>
                            <span className="font-medium">{profile.family.fatherEducation || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Status:</span>
                            <Badge className={profile.family.fatherAlive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                              {profile.family.fatherAlive ? "Living" : "Not Living"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="bg-pink-50 p-4 rounded-lg">
                        <h4 className="font-medium text-pink-700 mb-2">Mother's Details</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Name:</span>
                            <span className="font-medium">{profile.family.motherName || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Occupation:</span>
                            <span className="font-medium">{profile.family.motherOccupation || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Education:</span>
                            <span className="font-medium">{profile.family.motherEducation || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Status:</span>
                            <Badge className={profile.family.motherAlive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                              {profile.family.motherAlive ? "Living" : "Not Living"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Siblings Information */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Users className="w-4 h-4 mr-2 text-blue-500" />
                    Siblings Information
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-100 border border-blue-200 rounded-lg p-4 text-center">
                      <h4 className="font-medium text-blue-800 text-sm">Brothers</h4>
                      <p className="text-2xl font-bold text-blue-600">{profile.family.brothersCount || 0}</p>
                      <p className="text-xs text-blue-600 mt-1">
                        Married: {profile.family.marriedBrothers || 0}
                      </p>
                    </div>
                    <div className="bg-pink-100 border border-pink-200 rounded-lg p-4 text-center">
                      <h4 className="font-medium text-pink-800 text-sm">Sisters</h4>
                      <p className="text-2xl font-bold text-pink-600">{profile.family.sistersCount || 0}</p>
                      <p className="text-xs text-pink-600 mt-1">
                        Married: {profile.family.marriedSisters || 0}
                      </p>
                    </div>
                    <div className="bg-purple-100 border border-purple-200 rounded-lg p-4 text-center">
                      <h4 className="font-medium text-purple-800 text-sm">Unmarried Bros</h4>
                      <p className="text-2xl font-bold text-purple-600">{profile.family.unmarriedBrothers || 0}</p>
                    </div>
                    <div className="bg-amber-100 border border-amber-200 rounded-lg p-4 text-center">
                      <h4 className="font-medium text-amber-800 text-sm">Unmarried Sisters</h4>
                      <p className="text-2xl font-bold text-amber-600">{profile.family.unmarriedSisters || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Family Background */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Shield className="w-4 h-4 mr-2 text-purple-500" />
                    Family Background
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Family Type</h4>
                      <p className="text-gray-800">{profile.family.familyType || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Family Status</h4>
                      <p className="text-gray-800">{profile.family.familyStatus || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Family Values</h4>
                      <p className="text-gray-800">{profile.family.familyValues || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Family Income</h4>
                      <p className="text-gray-800">{profile.family.familyIncome || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Native Place</h4>
                      <p className="text-gray-800">{profile.family.nativePlace || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Family Living In</h4>
                      <p className="text-gray-800">{profile.family.familyLivingIn || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* About Family */}
                {profile.family.aboutFamily && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                      <Heart className="w-4 h-4 mr-2 text-pink-500" />
                      About Family
                    </h3>
                    <p className="text-gray-600 bg-green-50 p-4 rounded-lg leading-relaxed">
                      {profile.family.aboutFamily}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Address Details Section */}
          {selectedSection === 'address' && (
            <Card className={`shadow-xl border-0 overflow-hidden ${!userPaymentStatus.canViewContacts ? 'relative' : ''}`}>
              <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Home className="w-6 h-6 mr-3" />
                  Address Details
                  {/* Status Badge */}
                  <Badge className={`ml-3 ${!userPaymentStatus.canViewContacts ? 'bg-red-500 animate-pulse' : 'bg-green-500'} text-white text-xs`}>
                    {!userPaymentStatus.canViewContacts ? '🔒 PREMIUM REQUIRED' : '✅ UNLOCKED'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className={`p-6 space-y-6 relative ${!userPaymentStatus.canViewContacts ? 'blur-sm' : ''}`}>
                {/* Payment Lock Overlay */}
                {!userPaymentStatus.canViewContacts && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10 rounded-lg">
                    <button 
                      onClick={() => navigate('/payment')}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg"
                      title="Click to upgrade to premium and unlock address details"
                    >
                      <Award className="w-5 h-5 mr-2 inline" />
                      🚀 Address details require premium membership - Click to upgrade
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Current Address */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                      Current Address
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-700 mb-2">Address Line</h4>
                        <p className="text-gray-800">
                          {[profile.address.buildingNo, profile.address.buildingName, profile.address.areaName, profile.address.street]
                            .filter(Boolean)
                            .join(', ') || '—'}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <h4 className="font-medium text-gray-700 text-sm">City</h4>
                          <p className="text-gray-800">{profile.address.city || '—'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <h4 className="font-medium text-gray-700 text-sm">State</h4>
                          <p className="text-gray-800">{profile.address.state || '—'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <h4 className="font-medium text-gray-700 text-sm">Pincode</h4>
                          <p className="text-gray-800">{profile.address.pincode || '—'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <h4 className="font-medium text-gray-700 text-sm">Country</h4>
                          <p className="text-gray-800">{profile.address.country || '—'}</p>
                        </div>
                      </div>
                      {profile.address.landmark && (
                        <div className="bg-orange-50 p-3 rounded-lg">
                          <h4 className="font-medium text-orange-700 text-sm">Landmark</h4>
                          <p className="text-gray-800">{profile.address.landmark}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Additional Address Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                      <Building className="w-4 h-4 mr-2 text-amber-500" />
                      Additional Information
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-700 mb-2">Residence Type</h4>
                        <p className="text-gray-800">{profile.address.residence || '—'}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-700 mb-2">Address Type</h4>
                        <p className="text-gray-800">{profile.address.addressType || '—'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Habits & Lifestyle Section */}
          {selectedSection === 'habits' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-pink-500 to-rose-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Heart className="w-6 h-6 mr-3" />
                  Habits & Lifestyle
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Lifestyle Habits */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Activity className="w-4 h-4 mr-2 text-pink-500" />
                    Lifestyle Habits
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-green-50 border border-green-200 p-4 rounded-lg text-center">
                      <h4 className="font-medium text-green-700 mb-2">Diet</h4>
                      <p className="text-lg font-semibold text-green-800">{profile.personal.diet || '—'}</p>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg text-center">
                      <h4 className="font-medium text-orange-700 mb-2">Smoking</h4>
                      <p className="text-lg font-semibold text-orange-800">{profile.personal.smoke || '—'}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-center">
                      <h4 className="font-medium text-blue-700 mb-2">Drinking</h4>
                      <p className="text-lg font-semibold text-blue-800">{profile.personal.drink || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Physical Attributes */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <User className="w-4 h-4 mr-2 text-purple-500" />
                    Physical Attributes
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Body Type</h4>
                      <p className="text-gray-800">{profile.personal.bodyType || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Complexion</h4>
                      <p className="text-gray-800">{profile.personal.complexion || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Physical Status</h4>
                      <p className="text-gray-800">{profile.personal.physicalStatus || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Blood Group</h4>
                      <p className="text-gray-800">{profile.personal.bloodGroup || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Height</h4>
                      <p className="text-gray-800">{profile.basic.height ? `${profile.basic.height} cm` : '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-700 mb-1">Weight</h4>
                      <p className="text-gray-800">{profile.basic.weight ? `${profile.basic.weight} kg` : '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Hobbies & Interests */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Star className="w-4 h-4 mr-2 text-yellow-500" />
                    Hobbies & Interests
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h4 className="font-medium text-purple-700 mb-2">Hobbies</h4>
                      <p className="text-gray-800">{profile.personal.hobbies || '—'}</p>
                    </div>
                    <div className="bg-pink-50 p-4 rounded-lg">
                      <h4 className="font-medium text-pink-700 mb-2">Interests</h4>
                      <p className="text-gray-800">{profile.personal.interests || '—'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Astrological Details Section */}
          {selectedSection === 'astrological' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Star className="w-6 h-6 mr-3" />
                  Astrological Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Birth Details */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-yellow-500" />
                    Birth Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-700 mb-1">Birth Time</h4>
                      <p className="text-gray-800">{profile.personal.birthTime || '—'}</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-700 mb-1">Birth Place</h4>
                      <p className="text-gray-800">{profile.personal.birthPlace || '—'}</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-700 mb-1">Rashi</h4>
                      <p className="text-gray-800">{profile.personal.rashi || '—'}</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-700 mb-1">Nakshatra</h4>
                      <p className="text-gray-800">{profile.personal.nakshatra || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Astrological Information */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Star className="w-4 h-4 mr-2 text-orange-500" />
                    Astrological Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Sub Caste</h4>
                      <p className="text-gray-800">{profile.personal.subCaste || '—'}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Gotra</h4>
                      <p className="text-gray-800">{profile.personal.gotra || '—'}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Manglik</h4>
                      <p className="text-gray-800">{profile.personal.manglik || '—'}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Charan</h4>
                      <p className="text-gray-800">{profile.personal.charan || '—'}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Gan</h4>
                      <p className="text-gray-800">{profile.personal.gan || '—'}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-orange-700 mb-1">Nadi</h4>
                      <p className="text-gray-800">{profile.personal.nadi || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Horoscope Match */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <HeartHandshake className="w-4 h-4 mr-2 text-pink-500" />
                    Horoscope Preferences
                  </h3>
                  <div className="bg-pink-50 p-4 rounded-lg">
                    <h4 className="font-medium text-pink-700 mb-1">Horoscope Match</h4>
                    <p className="text-gray-800">{profile.personal.horoscopeMatch || '—'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Documents Section */}
          {selectedSection === 'documents' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <FileText className="w-6 h-6 mr-3" />
                  Documents
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {!userPaymentStatus.canViewContacts ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Shield className="w-10 h-10 text-red-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Premium Content Locked</h3>
                    <p className="text-gray-600 mb-6">
                      Documents are available for Premium users only.<br />
                      Upgrade your account to view this profile's documents.
                    </p>
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
                      onClick={() => navigate('/payment')}
                    >
                      <Award className="w-5 h-5 mr-2" />
                      Upgrade to Premium
                    </Button>
                  </div>
                ) : profile.documents.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.documents.map((doc) => (
                      <div key={doc.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-800 mb-1">{doc.documentType}</h4>
                            <p className="text-sm text-gray-600 mb-2">{doc.documentFileName}</p>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <span>{(doc.documentSize / (1024 * 1024)).toFixed(1)} MB</span>
                              <span>•</span>
                              <span>{doc.documentFormat.toUpperCase()}</span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="bg-transparent border-blue-300 text-blue-700 hover:bg-blue-50"
                              onClick={() => {
                                // Open document in new tab
                                window.open(getMediaUrl(doc.documentUrl), '_blank');
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500">No documents uploaded</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Partner Expectations Section */}
          {selectedSection === 'partner' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <HeartHandshake className="w-6 h-6 mr-3" />
                  Partner Expectations
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {Object.keys(profile.partnerExpectations).length > 0 ? (
                  <>
                    {/* Age & Height Preferences */}
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                        <User className="w-4 h-4 mr-2 text-purple-500" />
                        Age & Physical Preferences
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(profile.partnerExpectations as any).minAge && (
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <h4 className="font-medium text-purple-700 mb-2">Minimum Age</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).minAge} years</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).maxAge && (
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <h4 className="font-medium text-purple-700 mb-2">Maximum Age</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).maxAge} years</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).minHeight && (
                          <div className="bg-pink-50 p-4 rounded-lg">
                            <h4 className="font-medium text-pink-700 mb-2">Minimum Height</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).minHeight} cm</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).maxHeight && (
                          <div className="bg-pink-50 p-4 rounded-lg">
                            <h4 className="font-medium text-pink-700 mb-2">Maximum Height</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).maxHeight} cm</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).minWeight && (
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <h4 className="font-medium text-blue-700 mb-2">Minimum Weight</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).minWeight} kg</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).maxWeight && (
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <h4 className="font-medium text-blue-700 mb-2">Maximum Weight</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).maxWeight} kg</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Personal Attributes */}
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                        <Heart className="w-4 h-4 mr-2 text-pink-500" />
                        Personal Attributes
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(profile.partnerExpectations as any).maritalStatus && (
                          <div className="bg-rose-50 p-4 rounded-lg">
                            <h4 className="font-medium text-rose-700 mb-2">Marital Status</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).maritalStatus}</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).religion && (
                          <div className="bg-amber-50 p-4 rounded-lg">
                            <h4 className="font-medium text-amber-700 mb-2">Religion</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).religion}</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).caste && (
                          <div className="bg-lime-50 p-4 rounded-lg">
                            <h4 className="font-medium text-lime-700 mb-2">Caste</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).caste}</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).motherTongue && (
                          <div className="bg-teal-50 p-4 rounded-lg">
                            <h4 className="font-medium text-teal-700 mb-2">Mother Tongue</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).motherTongue}</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).bodyType && (
                          <div className="bg-cyan-50 p-4 rounded-lg">
                            <h4 className="font-medium text-cyan-700 mb-2">Body Type</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).bodyType}</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).complexion && (
                          <div className="bg-sky-50 p-4 rounded-lg">
                            <h4 className="font-medium text-sky-700 mb-2">Complexion</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).complexion}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Education & Career */}
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                        <GraduationCap className="w-4 h-4 mr-2 text-indigo-500" />
                        Education & Career
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(profile.partnerExpectations as any).education && (
                          <div className="bg-indigo-50 p-4 rounded-lg">
                            <h4 className="font-medium text-indigo-700 mb-2">Education</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).education}</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).profession && (
                          <div className="bg-violet-50 p-4 rounded-lg">
                            <h4 className="font-medium text-violet-700 mb-2">Profession</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).profession}</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).employedIn && (
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <h4 className="font-medium text-purple-700 mb-2">Employed In</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).employedIn}</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).annualIncome && (
                          <div className="bg-fuchsia-50 p-4 rounded-lg">
                            <h4 className="font-medium text-fuchsia-700 mb-2">Annual Income</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).annualIncome}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Location Preferences */}
                    {(profile.partnerExpectations as any).city || (profile.partnerExpectations as any).state || (profile.partnerExpectations as any).country && (
                      <div>
                        <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                          <MapPin className="w-4 h-4 mr-2 text-green-500" />
                          Location Preferences
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {(profile.partnerExpectations as any).city && (
                            <div className="bg-green-50 p-4 rounded-lg">
                              <h4 className="font-medium text-green-700 mb-2">City</h4>
                              <p className="text-gray-800">{(profile.partnerExpectations as any).city}</p>
                            </div>
                          )}
                          {(profile.partnerExpectations as any).state && (
                            <div className="bg-emerald-50 p-4 rounded-lg">
                              <h4 className="font-medium text-emerald-700 mb-2">State</h4>
                              <p className="text-gray-800">{(profile.partnerExpectations as any).state}</p>
                            </div>
                          )}
                          {(profile.partnerExpectations as any).country && (
                            <div className="bg-lime-50 p-4 rounded-lg">
                              <h4 className="font-medium text-lime-700 mb-2">Country</h4>
                              <p className="text-gray-800">{(profile.partnerExpectations as any).country}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Lifestyle Preferences */}
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                        <Activity className="w-4 h-4 mr-2 text-orange-500" />
                        Lifestyle Preferences
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(profile.partnerExpectations as any).diet && (
                          <div className="bg-orange-50 p-4 rounded-lg">
                            <h4 className="font-medium text-orange-700 mb-2">Diet</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).diet}</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).smoke && (
                          <div className="bg-red-50 p-4 rounded-lg">
                            <h4 className="font-medium text-red-700 mb-2">Smoking</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).smoke}</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).drink && (
                          <div className="bg-amber-50 p-4 rounded-lg">
                            <h4 className="font-medium text-amber-700 mb-2">Drinking</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).drink}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Family Preferences */}
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                        <Users className="w-4 h-4 mr-2 text-blue-500" />
                        Family Preferences
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(profile.partnerExpectations as any).familyType && (
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <h4 className="font-medium text-blue-700 mb-2">Family Type</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).familyType}</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).familyStatus && (
                          <div className="bg-indigo-50 p-4 rounded-lg">
                            <h4 className="font-medium text-indigo-700 mb-2">Family Status</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).familyStatus}</p>
                          </div>
                        )}
                        {(profile.partnerExpectations as any).familyValues && (
                          <div className="bg-violet-50 p-4 rounded-lg">
                            <h4 className="font-medium text-violet-700 mb-2">Family Values</h4>
                            <p className="text-gray-800">{(profile.partnerExpectations as any).familyValues}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Astrological Preferences */}
                    {((profile.partnerExpectations as any).manglik || (profile.partnerExpectations as any).horoscopeMatch) && (
                      <div>
                        <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                          <Star className="w-4 h-4 mr-2 text-yellow-500" />
                          Astrological Preferences
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {(profile.partnerExpectations as any).manglik && (
                            <div className="bg-yellow-50 p-4 rounded-lg">
                              <h4 className="font-medium text-yellow-700 mb-2">Manglik</h4>
                              <p className="text-gray-800">{(profile.partnerExpectations as any).manglik}</p>
                            </div>
                          )}
                          {(profile.partnerExpectations as any).horoscopeMatch && (
                            <div className="bg-amber-50 p-4 rounded-lg">
                              <h4 className="font-medium text-amber-700 mb-2">Horoscope Match</h4>
                              <p className="text-gray-800">{(profile.partnerExpectations as any).horoscopeMatch}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* About Partner */}
                    {(profile.partnerExpectations as any).aboutPartner && (
                      <div>
                        <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                          <HeartHandshake className="w-4 h-4 mr-2 text-pink-500" />
                          About Partner
                        </h3>
                        <p className="text-gray-600 bg-pink-50 p-4 rounded-lg leading-relaxed">
                          {(profile.partnerExpectations as any).aboutPartner}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <HeartHandshake className="w-8 h-8 text-purple-400" />
                    </div>
                    <p className="text-gray-500">No partner expectations specified</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Videos Section */}
          {selectedSection === 'videos' && (
            <Card className="shadow-xl border-0 overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-red-500 to-pink-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Video className="w-6 h-6 mr-3" />
                  Videos
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {!userPaymentStatus.canViewContacts ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Shield className="w-10 h-10 text-red-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Premium Content Locked</h3>
                    <p className="text-gray-600 mb-6">
                      Videos are available for Premium users only.<br />
                      Upgrade your account to view this profile's videos.
                    </p>
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
                      onClick={() => navigate('/payment')}
                    >
                      <Award className="w-5 h-5 mr-2" />
                      Upgrade to Premium
                    </Button>
                  </div>
                ) : profile.videos && profile.videos.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {profile.videos.map((video) => (
                      <div key={video.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow">
                        <div className="relative bg-gray-900 aspect-video">
                          <video
                            controls
                            className="w-full h-full object-contain"
                            src={video.videoUrl?.startsWith('http') ? video.videoUrl : `${API_BASE}${video.videoUrl}`}
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                        <div className="p-4 space-y-2">
                          {video.videoTitle && (
                            <h4 className="font-semibold text-gray-800 truncate">{video.videoTitle}</h4>
                          )}
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {video.videoDuration || 'Duration N/A'}
                            </span>
                            {video.videoSize && (
                              <span>{(video.videoSize / (1024 * 1024)).toFixed(2)} MB</span>
                            )}
                          </div>
                          {video.videoFormat && (
                            <div className="text-xs text-gray-400">
                              Format: {video.videoFormat.toUpperCase()}
                            </div>
                          )}
                          {video.status && (
                            <Badge variant={video.status === 'approved' ? 'default' : 'secondary'} className="text-xs">
                              {video.status}
                            </Badge>
                          )}
                          {video.createdAt && (
                            <p className="text-xs text-gray-400">
                              Uploaded: {new Date(video.createdAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Video className="w-8 h-8 text-red-400" />
                    </div>
                    <p className="text-gray-500">No videos uploaded</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Chat Modal (user-initiated) */}
      {chatOpen && chatConversationWith && (
        <ChatModal
          /**
           * NOTE:
           * ChatModal expects `otherUserId` and `otherDisplayName` (optional) and `visible` props.
           * Provide the name that the parent already has so the modal shows it immediately
           * instead of attempting to fetch (which can fail and result in "Loading..." being shown).
           * Pass canSendMessages based on payment status to allow viewing but restrict sending.
           */
          otherUserId={chatConversationWith.id}
          otherDisplayName={chatConversationWith.name}
          visible={true}
          canSendMessages={userPaymentStatus.canViewContacts}
          onUpgradeClick={() => navigate('/payment')}
          onClose={() => {
            setChatOpen(false);
            setChatConversationWith(null);
          }}
        />
      )}

      {/* Full Screen Photo Modal - NEW */}
      {showFullPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowFullPhoto(false)}
        >
          <div className="relative max-w-6xl max-h-full">
            <Button
              variant="ghost"
              className="absolute -top-12 right-0 text-white hover:bg-white/20"
              onClick={() => setShowFullPhoto(false)}
            >
              <X className="w-6 h-6" />
            </Button>
            
            <div className="relative">
              <img 
                src={getMediaUrl(profile.photos[activePhotoIndex]?.photoUrl)} 
                alt={`Photo ${activePhotoIndex + 1}`}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
              
              {/* Watermark in fullscreen */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <img 
                  src="/images/RJBD.png"
                  alt="RJBD Watermark"
                  className="h-20 w-auto opacity-30"
                />
              </div>
            </div>
            
            {/* Navigation Buttons in Fullscreen */}
            {profile.photos.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePhotoIndex((prev) => (prev - 1 + profile.photos.length) % profile.photos.length);
                  }}
                >
                  <ChevronLeft className="w-8 h-8" />
                </Button>
                <Button
                  variant="ghost"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePhotoIndex((prev) => (prev + 1) % profile.photos.length);
                  }}
                >
                  <ChevronRight className="w-8 h-8" />
                </Button>
              </>
            )}
            
            {/* Photo Counter in Fullscreen */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full">
              <p className="text-sm font-medium">
                {activePhotoIndex + 1} / {profile.photos.length}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Footer with additional actions */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between">
            <div className="text-center sm:text-left mb-4 sm:mb-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Report Profile</h3>
              <p className="text-gray-600 text-sm">If you find any issues with this profile</p>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                <AlertCircle className="w-4 h-4 mr-2" />
                Report Issue
              </Button>
              <Button variant="outline" disabled className="border-gray-300 text-gray-400 cursor-not-allowed opacity-60">
                <Share2 className="w-4 h-4 mr-2" />
                Share Profile
              </Button>
            </div>
          </div> 
        </div>
      </div>
    </div>
  );
}